import {ForecastTime} from 'src/lib/api/series';
import {getLatestForecastTime} from 'src/lib/dates';
import {nearestForecastTimes} from 'src/lib/h5/forecastTimes';

export type ModelRuns<L> = {
  listing: L;
  forecasts: ForecastTime[];
  // The anchor's runs a compared model is paired with, whether or not it had any to pair.
  comparedTo?: ForecastTime[];
};

const newestFirst = (forecasts: ForecastTime[]) =>
  forecasts.toSorted((a, b) => b.initializationTime.getTime() - a.initializationTime.getTime());

/**
 * The runs each model draws. The anchor draws the selected runs. Models run on different
 * schedules, so each other model draws a run paired with each selected one, as the legacy chart
 * pairs weather with flow: the anchor's newest run shows each model's newest, so a live event
 * shows every model's latest; any other run shows the nearest within a day. Two selected runs
 * near one of the model's share it.
 */
export function pairModelRuns<L extends {availableForecasts: ForecastTime[]}>(
  listings: L[],
  anchor: L | undefined,
  selected: ForecastTime[]
): ModelRuns<L>[] {
  const anchorNewest = anchor && getLatestForecastTime(anchor.availableForecasts);
  const selectedInitTimes = new Set(
    selected.map((forecast) => forecast.initializationTime.getTime())
  );
  return listings.map((listing) => {
    if (listing === anchor) {
      return {
        listing,
        forecasts: listing.availableForecasts.filter((forecast) =>
          selectedInitTimes.has(forecast.initializationTime.getTime())
        ),
      };
    }
    if (selected.length === 0) return {listing, forecasts: []};
    const paired = new Set(
      nearestForecastTimes(selected, listing.availableForecasts, anchorNewest)
    );
    // Newest first, as listings are.
    const forecasts = listing.availableForecasts.filter((forecast) => paired.has(forecast));
    return {listing, forecasts, comparedTo: newestFirst(selected)};
  });
}
